import React, { LegacyRef } from "react";
import "./Button.scss";

interface IProps {
    id?: string;
    variant: TButtonVariant | string;
    children?: React.ReactNode | undefined;
    size?: "small" | "medium" | "large";
    iconOnly?: boolean | undefined;
    active?: boolean | undefined;
}

export type TButtonVariant =
    | "primary"
    | "secondary"
    | "danger"
    | "success"
    | "warning"
    | "dark"
    | "ghost"
    | "navigation";

const extractLabel = (nodes: React.ReactNode): string | null => {
    for (const child of React.Children.toArray(nodes)) {
        if (typeof child === "string" || typeof child === "number") {
            const text = child.toString().trim();
            if (text) return text;
        }

        if (React.isValidElement(child)) {
            const icon = (child.props as { icon?: unknown })?.icon;
            if (icon && typeof icon === "object" && "iconName" in icon && (icon as { iconName: string }).iconName) {
                return (icon as { iconName: string }).iconName;
            }
            if (typeof icon === "string") {
                return icon;
            }

            const labelled =
                (child.props as { ["aria-label"]?: unknown; title?: unknown })["aria-label"] ??
                (child.props as { ["aria-label"]?: unknown; title?: unknown }).title;
            if (labelled) {
                const text = String(labelled).trim();
                if (text) return text;
            }

            if (child.props?.children) {
                const nested = extractLabel(child.props.children);
                if (nested) return nested;
            }
        }
    }

    return null;
};

const resolveId = (explicitId: string | undefined, children: React.ReactNode): string => {
    if (explicitId && explicitId.trim()) {
        return explicitId.trim();
    }

    const derived = extractLabel(children) ?? "button";
    return derived.trim().replace(/\s+/g, "-").toLowerCase();
};

const isIconOnlyChild = (children: React.ReactNode): boolean => {
    const nodes = React.Children.toArray(children).filter((child) => {
        if (typeof child === "string" || typeof child === "number") {
            return child.toString().trim().length > 0;
        }
        return child != null;
    });

    if (nodes.length !== 1) return false;

    const onlyChild = nodes[0];
    if (!React.isValidElement(onlyChild)) return false;

    const props = onlyChild.props as { icon?: unknown };
    if (!props?.icon) return false;

    const type = onlyChild.type as { displayName?: string; name?: string };
    const componentName = type?.displayName ?? type?.name;
    return componentName === "FontAwesomeIcon";
};

// eslint-disable-next-line react/display-name
const Button = React.forwardRef(
    (
        props: IProps & React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
        ref: LegacyRef<HTMLButtonElement>,
    ) => {
        const { id, children, className, variant, iconOnly, active, size = "medium", ...rest } = props;
        const resolvedId = resolveId(id, children);
        const resolvedIconOnly = iconOnly ?? isIconOnlyChild(children);
        const combinedClassName = `userful-button ${className || ""} ${variant ? variant + "-button" : ""} ${size}-size-button${resolvedIconOnly ? " icon-button" : ""}${active ? " userful-active-button" : ""}`;

        return (
            <button id={`${resolvedId}-button`} {...rest} className={combinedClassName} ref={ref}>
                {children}
            </button>
        );
    },
);
export default Button;
